import {
  Button, Box, Container, Paper, Checkbox, InputAdornment, IconButton, Collapse,
  TextField, Stack, List, ListItem, ListItemText, Table, TableBody, TableCell, Grid,
  CircularProgress, TableContainer, TableHead, TableRow, Typography, TablePagination,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCandidateList } from 'api/admin';
import exportFromJSON from 'export-from-json';
import TypeAhead from 'components/TypeAhead';
import { endpoints } from 'utils/axios';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { useSnackbar } from 'components/snackbar';
import SearchIcon from '@mui/icons-material/Search';
import dayjs from 'dayjs';

const statusReverseMap = {
  high: 'urgent',
  medium: 'passive',
  low: 'exceptional',
  none: 'not_looking',
};

const statusOptions = [
  {
    value: 'urgent',
    primary: 'I am actively searching for a new job',
    secondary: '(We are on it. You’ll hear from us frequently)',
  },
  {
    value: 'passive',
    primary: 'I’m open to interesting opportunities but not actively searching',
    secondary: '(We’ll send you suitable job opportunities a couple of times each month.)',
  },
  // {
  //     value: 'exceptional',
  //     primary: 'I’ll consider only if something exceptional comes along',
  //     secondary: '(We will share only highly suitable jobs once in a while)',
  // },
  {
    value: 'not_looking',
    primary: 'Not looking out. Have a great job already',
    secondary: '(That’s wonderful to hear. Update your search option when you need help)',
  },
];

const getJobSearchPrimary = (status) => {
  if (!status) return 'N/A';
  const mappedValue = statusReverseMap[status]; // e.g., "urgent"
  const option = statusOptions.find(opt => opt.value === mappedValue);
  return option ? option.primary : 'N/A';
};

/**
 * @function
 * @description Component to show a list of all available candidates. It also
 * provides a search functionality to filter candidates by various criteria.
 * The component also allows the user to download the selected candidates' data
 * in a CSV format.
 * @param {Object} props - Component props
 * @prop {Function} handleNavigation - Function to handle navigation between pages
 * @returns {ReactElement} - The rendered component
 */
const CandidatesList = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [candidateList, setCandidateList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchParameter, setSearchParameter] = useState(false);
  const [degreeData, setDegreeData] = useState([]);
  const [roleData, setRoleData] = useState([]);

  const [page, setPage] = useState(0);
  const rowsPerPage = 20;

  const defaultValues = {
    candidateName: "",
    skills: [],
    functions: [],
    role: '',
    education: [],
    location: [],
    currentSalary: '',
    desiredSalary: '',
    desiredLocation: [],
    jobFunction: '',
    min_experience: null,
    max_experience: null,
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const { control, register, handleSubmit, setValue, watch, trigger, getValues, reset, formState: { errors, isDirty, isValid, dirtyFields, isSubmitting }, } = methods;

  useEffect(() => {
    fetchCandidateList();
    fetchDegreeData();
    fetchRoleData();
  }, []);

  /**
   * Fetches the list of all available candidates. It updates the component's state
   * with the fetched data and handles any errors that may occur during the request.
   * The function is called when the component mounts.
   * @returns {undefined}
   */
  const fetchCandidateList = async () => {
    try {
      const response = await getCandidateList();
      if (response?.success && response.data != null) {
        console.log(response.data);
        setCandidateList(response.data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
    setLoading(false);
  };

  /**
   * Downloads a CSV file with the selected candidates data, including the candidate's
   * profile information, education, work experience, skills, and job preferences.
   * 
   * @returns {undefined}
   */
  const downloadSelectedCandidatesData = () => {
    const selectedCandidatesData = candidateList?.filter(candidate => selectedCandidates?.includes(candidate?.id));

    const CandidatesList = selectedCandidatesData.map(candidate => {
      return {
        'Candidate Id': candidate?.id || 'N/A',
        'Profile Status': candidate?.user?.stages >= 4 ? 'complete' : 'incomplete',
        'Date of profile creation': dayjs(candidate?.user?.createdAt).format('DD MMM, YYYY') || 'N/A',
        'Name': candidate?.full_name || 'N/A',
        'Gender': candidate?.user?.sex || 'N/A',
        'Disability': candidate?.user?.disability || 'N/A',
        'DOB': candidate?.user?.date_of_birth ? dayjs(candidate.user.date_of_birth).format('DD MMM, YYYY') : 'N/A',
        'Current Location': candidate?.location || 'N/A',
        'Phone number': candidate?.phone || 'N/A',
        'Email id': candidate?.email || 'N/A',
        'LinkedIn URL': candidate?.user?.linkedin_profile || 'N/A',
        'Last education (Institution Name)': candidate?.user?.candidate?.educations?.[0]?.education_institute_name || 'N/A',
        'Last education (degree name)': candidate?.user?.candidate?.educations?.[0]?.degree_received || 'N/A',
        'Last education start and end dates':
          candidate?.user?.candidate?.educations?.[0]?.education_from &&
            candidate?.user?.candidate?.educations?.[0]?.education_to
            ? `${dayjs(candidate.user.candidate.educations[0].education_from).format('MMM YYYY')} - ${dayjs(candidate.user.candidate.educations[0].education_to).format('MMM YYYY')}`
            : 'N/A',
        'Work experience (Current Company Name)': candidate?.workHistory?.[0]?.organization_name || 'N/A',
        'Current Designation': candidate?.designationRole || 'N/A',
        'Current Job Function': candidate?.jobFunction || 'N/A',
        'Job Type': (() => {
          const jobType = candidate?.workHistory?.[0]?.job_type;
          if (!jobType) return 'N/A';
          const value = jobType.trim().toLowerCase();
          if (value === 'both') {
            return 'Full Time, Part Time, Temporary, Internship';
          } else if (value === 'Full time') {
            return 'Full Time';
          } else if (value === 'Part time') {
            return 'Part Time, Temporary, Internship';
          }
          return jobType || 'N/A';
        })(),
        'Current Job Location': candidate?.workHistory?.[0]?.work_history_location || 'N/A',
        'People managed': candidate?.workHistory?.[0]?.team_size_managed || 'N/A',
        'Last work experience start and end dates': candidate?.workHistory?.[0]?.work_history_from && candidate?.workHistory?.[0]?.work_history_to
          ? `${dayjs(candidate?.workHistory?.[0]?.work_history_from).format('MMM YYYY')} - ${dayjs(candidate?.workHistory?.[0].work_history_to).format('MMM YYYY')}`
          : 'N/A',
        'Total work experience (Years)': candidate?.totalExperience || 'N/A',
        'Expertise areas': [
          candidate?.user?.candidate?.educations?.[0]?.all_skills,
          candidate?.user?.candidate?.work_history?.[0]?.all_skills,
          candidate?.user?.candidate?.certificate?.[0]?.all_skills
        ]
          .filter(Boolean)
          .flat()
          .join(', ') || 'N/A',
        'Professional Summary': candidate?.user?.candidate?.summary || 'N/A',
        'Resume Link': candidate?.user?._id
          ? `=HYPERLINK("${process.env.REACT_APP_END_POINT}/api/services/download-candidate-resume?user_id=${candidate.user._id}", "Download Resume")`
          : 'N/A',
        'Candidate feedback': candidate?.feedback?.length
          ? candidate.feedback.map(f => f?.feedback).join(", ")
          : 'N/A',
        // 'Salary': candidate?.salary || 'N/A',
        'Job preference': (() => {
          const jobType = candidate?.user?.candidate?.job_preferences?.job_type?.[0];
          if (!jobType) return 'N/A';
          const value = jobType.trim().toLowerCase();
          if (value === 'both') {
            return 'Full Time, Part Time, Temporary, Internship';
          } else if (value === 'full time') {
            return 'Full Time';
          } else if (value === 'part time') {
            return 'Part Time, Temporary, Internship';
          }
          return jobType || 'N/A';
        })(),
        'Preferred work format': candidate?.user?.candidate?.job_preferences?.work_format?.[0] || 'N/A',
        'Preferred working hours': candidate?.user?.candidate?.job_preferences?.preferred_job_shifts?.length
          ? candidate.user.candidate.job_preferences.preferred_job_shifts.join(", ")
          : 'N/A',
        'Preferred locations': candidate?.user?.candidate?.job_preferences?.prefered_location?.length
          ? candidate.user.candidate.job_preferences.prefered_location.join(", ")
          : 'N/A',
        'Current compensation - Fixed': candidate?.user?.candidate?.job_preferences?.fixed_ctc || 'N/A',
        'Current compensation - variable': candidate?.user?.candidate?.job_preferences?.variable_ctc || 'N/A',
        'Expected annual compensation': candidate?.user?.candidate?.job_preferences?.expected_ctc || 'N/A',
        'Notice period': candidate?.user?.candidate?.job_preferences?.notice_period || 'N/A',
        'Part - time Hourly rate': candidate?.user?.candidate?.job_preferences?.hourly_rate || 'N/A',
        // 'Job Function': candidate?.jobFunction || 'N/A',
        'Part - time availability(hours / week)': candidate?.user?.candidate?.job_preferences?.hour || 'N/A',
        'Job Search Status': getJobSearchPrimary(candidate?.job_search_status)
      };
    });

    const fileName = `candidateList_${Date.now()}`;
    const exportType = exportFromJSON.types.csv;
    exportFromJSON({ data: CandidatesList, fileName, exportType });
  };

  /**
   * Handles navigation to the specified path.
   * 
   * @param {string} path Path to navigate to.
   * 
   * @returns {undefined}
   */
  const handleNavigation = (path) => {
    navigate(path);
  };
  /**
   * Toggles the search parameter and resets the search form
   * when the search parameter is true.
   *
   * @returns {undefined}
   */
  const handleSearch = () => {
    setSearchParameter(prevState => !prevState);
    if (searchParameter) {
      setLoading(true);
      reset(defaultValues);
      fetchCandidateList();
    }
  };

  /**
   * Handles the click event on a candidate's row in the DataGrid component.
   * Navigates to the candidate's applications page using their unique user ID.
   *
   * @param {string} userId Unique identifier of the candidate.
   *
   * @returns {undefined}
   */

  const handleRowClick = (userId) => {
    handleNavigation(`/candidate-applications/${userId}`);
  };

  /**
   * Handles the checkbox change event for a particular candidate.
   * If the checkbox is checked, it adds the candidate's id to the selectedCandidates state.
   * If the checkbox is unchecked, it removes the candidate's id from the selectedCandidates state.
   * @param {object} event - The checkbox change event.
   * @param {string} candidateId - The id of the candidate.
   */
  const handleCheckboxChange = (event, candidateId) => {
    if (event.target.checked) {
      setSelectedCandidates([...selectedCandidates, candidateId]);
    } else {
      setSelectedCandidates(selectedCandidates.filter(id => id !== candidateId));
    }
  };

  /**
   * Handles the select all checkbox change event.
   * If the checkbox is checked, it sets the selectedCandidates state to include all candidate ids.
   * If the checkbox is unchecked, it clears the selectedCandidates state.
   * @param {object} event - The select all checkbox change event.
   */
  const handleSelectAllChange = (event) => {
    setSelectAll(event.target.checked);
    if (event.target.checked) {
      setSelectedCandidates(candidateList?.map(candidate => candidate?.id));
    } else {
      setSelectedCandidates([]);
    }
  };

  const onSubmit = handleSubmit(async (formData) => {

    try {
      const response = await getCandidateList(formData);
      if (response?.success && response.data != null) {
        setCandidateList(response.data);
      }
      else if (response?.success === false) {
        enqueueSnackbar('Failed to search', { variant: 'error' });
        console.error('Failed to search:', response.message);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      enqueueSnackbar('Failed to fetch candidates', { variant: 'error' });
    }
  });

  /**
   * Fetches the degree titles from the server and updates the degreeData state.
   * The degree titles are deduplicated using a Set.
   * @memberof CandidatesList
   * @returns {Promise<void>} - Resolves when the degrees are fetched successfully, rejects if there's an error.
   */
  const fetchDegreeData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.degrees}`);
    const resultData = await response.json();
    setDegreeData([...new Set([...resultData])]);
  };
  const fetchRoleData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.roles}`);
    const resultData = await response.json();
    setRoleData([...new Set([...resultData])]);
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Slice data for pagination
  const paginatedCandidates = candidateList?.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" style={{ height: '100vh' }}>
        <CircularProgress />
      </Grid>
    );
  };
  if (loading || !degreeData || !degreeData?.length > 0 || !roleData || !roleData?.length > 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '60px', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  };

  return (
    <FormProvider {...methods}>
      <Container style={{ marginTop: '80px', marginRight: '20px' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Typography variant="h5" component="div" sx={{ marginBottom: '1rem' }}>
            Candidate List
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <Button variant="contained" onClick={handleSearch}>
            {searchParameter ? "close search" : "search candidate"}
          </Button>
          <Button variant="contained" onClick={downloadSelectedCandidatesData} disabled={selectedCandidates.length === 0}>
            Download List
          </Button>
        </Stack>
        <Grid container spacing={2}>
          {searchParameter &&
            <Grid item xs={12} md={4}>
              <Paper elevation={3} sx={{ padding: '1rem' }}>
                <Typography variant="h6" sx={{ marginBottom: '0.5rem' }}>Search Parameters</Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <Stack spacing={2}>
                    <Button type="submit" variant="contained" disabled={isSubmitting}  ><SearchIcon /></Button>
                    <Controller
                      control={control}
                      name="candidateName"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Candidate Name"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="skills"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.skills}`}
                          multiple={true}
                          fullWidth
                          displayTextKey="name"
                          label="Select skills"
                          placeholder="Select skills"
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="role"
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          // api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.role}`}
                          label="Role/Designation"
                          placeholder="Select Role/Designation"
                          fullWidth
                        // displayTextKey='name'
                        // freeSolo={true}
                        // onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="education"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={degreeData}
                          label="Education"
                          placeholder="Select Qualification"
                          displayTextKey="display_name"
                          multiple={true}
                          disableClearable={true}
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="location"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.location}`}
                          label="Location"
                          placeholder="Select locations"
                          displayTextKey='name'
                          multiple={true}
                          fullWidth
                          onChange={(e, value) => field.onChange(value)}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="desiredSalary"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Min Desired Salary "
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="currentSalary"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Min Current Salary"
                          variant="outlined"
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="jobFunction"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={roleData}
                          label="Function"
                          size="small"
                          displayTextKey="name"
                          onChange={(e, value) => {
                            field.onChange(value?.display_name || value);
                          }}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="min_experience"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Min Experience Range"
                          variant="outlined"
                          type='number'
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="max_experience"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          size='small'
                          label="Max Experience Range"
                          variant="outlined"
                          type='number'
                        />
                      )}
                    />
                  </Stack>
                </form>
              </Paper>
            </Grid>}
          <Grid item xs={12} md={searchParameter ? 8 : 12}>
            {loading ? (
              <CircularProgress />
            ) : (
              <Paper elevation={3}>
                <TableContainer sx={{ maxHeight: 'calc(100vh - 220px)', overflow: 'auto' }}>
                  <Table stickyHeader >
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Checkbox
                            checked={selectAll}
                            onChange={handleSelectAllChange}
                          />
                        </TableCell>
                        <TableCell>#</TableCell>
                        <TableCell>Full Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Designation</TableCell>
                        <TableCell>Education </TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Current Annual Compensation</TableCell>
                        <TableCell>Part - time Hourly rate</TableCell>
                        <TableCell>Job Function</TableCell>
                        <TableCell>Download Resume</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCandidates?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10}>No candidate available.</TableCell>
                        </TableRow>
                      ) : (
                        paginatedCandidates?.map((candidate, index) => (
                          <TableRow key={candidate?.id} onClick={() => handleRowClick(candidate?.id)} sx={{ cursor: 'pointer' }}>
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox
                                checked={selectedCandidates.includes(candidate?.id)}
                                onChange={(event) => handleCheckboxChange(event, candidate?.id)}
                              />
                            </TableCell>
                            <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                            <TableCell>{candidate?.full_name}</TableCell>
                            <TableCell>{candidate?.email}</TableCell>
                            <TableCell>{candidate?.designationRole || 'N/A'}</TableCell>
                            <TableCell>{candidate?.educations || 'N/A'}</TableCell>
                            <TableCell>{candidate?.location || 'N/A'}</TableCell>
                            <TableCell>{candidate?.total_ctc || 'N/A'}</TableCell>
                            <TableCell>{candidate?.hourly_rate || 'N/A'}</TableCell>
                            <TableCell>{candidate?.jobFunction || 'N/A'}</TableCell>
                            <TableCell>
                              {candidate?.resumeLink ? (
                                <a
                                  href={`${process.env.REACT_APP_END_POINT}${candidate.resumeLink.replace('/fetch/', '/download/')}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Download Resume
                                </a>
                              ) : candidate?.user?._id ? (
                                <a
                                  href={`${process.env.REACT_APP_END_POINT}/api/services/download-candidate-resume?user_id=${candidate.user._id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Download Resume
                                </a>
                              ) : (
                                'N/A'
                              )}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Component */}
                <TablePagination
                  component="div"
                  count={candidateList?.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20]}
                />
              </Paper>
            )}
          </Grid>
        </Grid>
      </Container>
    </FormProvider>
  );
};

export default CandidatesList;